import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Database, Cloud } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { dataSourceService } from '../../services/dataSourceService';
import { DataSource, DataSourceType } from '../../types/dataSource';

interface DataSourceSelectorProps {
  /** Filter to only show sources of these types */
  typeFilter?: DataSourceType[];
  selectedSourceId: string | null;
  onSourceSelect: (source: DataSource | null) => void;
  /** Compact mode for embedding in flow nodes */
  compact?: boolean;
  /** Callback when user clicks "Add Source" */
  onAddSource?: () => void;
}

const typeIcons: Record<string, React.ReactNode> = {
  s3: <Cloud className="h-3 w-3 text-orange-500" />,
  gcp: <Cloud className="h-3 w-3 text-blue-500" />,
  postgresql: <Database className="h-3 w-3 text-indigo-500" />,
  mysql: <Database className="h-3 w-3 text-sky-500" />,
  sqlserver: <Database className="h-3 w-3 text-red-500" />,
};

export const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
  typeFilter,
  selectedSourceId,
  onSourceSelect,
  compact,
  onAddSource,
}) => {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dataSourceService
      .listSources()
      .then((all) => {
        const filtered = typeFilter
          ? all.filter((s) => typeFilter.includes(s.type))
          : all;
        setSources(filtered);
      })
      .catch(() => setSources([]))
      .finally(() => setLoading(false));
  }, [typeFilter?.join(',')]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading sources...
      </div>
    );
  }

  if (sources.length === 0) {
    return (
      <div className="text-xs text-muted-foreground space-y-1">
        <p>No data sources configured</p>
        {onAddSource && (
          <button
            onClick={onAddSource}
            className="text-primary hover:underline flex items-center gap-1"
          >
            <Plus className="h-3 w-3" />
            Add Source
          </button>
        )}
      </div>
    );
  }

  const handleChange = (value: string) => {
    if (value === '__add__') {
      onAddSource?.();
      return;
    }
    const source = sources.find((s) => s.source_id === value) || null;
    onSourceSelect(source);
  };

  return (
    <div className={compact ? 'nodrag' : ''}>
      <Select value={selectedSourceId || ''} onValueChange={handleChange}>
        <SelectTrigger className={compact ? 'h-7 text-[11px]' : 'h-9 text-sm'}>
          <SelectValue placeholder="Select a data source..." />
        </SelectTrigger>
        <SelectContent>
          {sources.map((s) => (
            <SelectItem key={s.source_id} value={s.source_id} className="text-xs">
              <span className="flex items-center gap-1.5">
                {typeIcons[s.type]}
                {s.name}
                {s.status === 'error' && (
                  <span className="text-destructive text-[9px]">(error)</span>
                )}
              </span>
            </SelectItem>
          ))}
          {onAddSource && (
            <SelectItem value="__add__" className="text-xs text-primary">
              <span className="flex items-center gap-1.5">
                <Plus className="h-3 w-3" />
                Add New Source
              </span>
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};
